// Read-only polling: keep useful observations, but never confuse them with live data.
const positive = (value: unknown, fallback: number): number =>
  typeof value === "string" && Number.isFinite(Number(value)) && Number(value) > 0
    ? Number(value)
    : fallback;

export const DEFAULT_OBSERVATION_POLICY = {
  pollMs: 5000,
  timeoutMs: 8000,
  freshMs: 15000,
  tickMs: 1000,
  indexerWarnMs: 10 * 60_000,
  onlineMs: 60_000,
  idleMs: 5 * 60_000,
};
export type ObservationPolicy = typeof DEFAULT_OBSERVATION_POLICY;
const env = import.meta.env;
export const OBSERVATION_POLICY: ObservationPolicy = Object.fromEntries(
  Object.entries(DEFAULT_OBSERVATION_POLICY).map(([key, fallback]) => [
    key, positive(env[`VITE_OBSERVATION_${key.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`], fallback),
  ]),
) as ObservationPolicy;

export interface Observation<T> {
  data: T | null;
  receivedAt: number | null;
  now: number;
  error: string | null;
}
export const emptyObservation = <T>(): Observation<T> => ({
  data: null, receivedAt: null, now: 0, error: null,
});
export const ageMs = <T>(state: Observation<T>): number =>
  state.receivedAt == null ? 0 : Math.max(0, state.now - state.receivedAt);
export const cached = <T>(state: Observation<T>, policy = OBSERVATION_POLICY): boolean =>
  state.data != null && (state.error != null || ageMs(state) >= policy.freshMs);
const AGE_UNITS = [[86_400_000, "d"], [3_600_000, "h"], [60_000, "m"], [1000, "s"]] as const;
export function ageLabel(ms: number): string {
  const age = Math.max(0, ms);
  const [scale, unit] = AGE_UNITS.find(([scale]) => age >= scale) ?? AGE_UNITS[3];
  return `${Math.floor(age / scale)}${unit} ago`;
}

// One request at a time. A timeout invalidates that request before the next poll;
// even a transport ignoring abort cannot publish late or after cleanup.
export function observe<T>(
  read: (signal: AbortSignal) => Promise<T>,
  publish: (state: Observation<T>) => void,
  policy = OBSERVATION_POLICY,
  clock = () => performance.now(),
): () => void {
  const run = {
    state: emptyObservation<T>(), stopped: false, generation: 0,
    controller: null as AbortController | null,
    next: undefined as ReturnType<typeof setTimeout> | undefined,
    deadline: undefined as ReturnType<typeof setTimeout> | undefined,
  };
  const emit = () => {
    if (run.stopped) return;
    run.state = { ...run.state, now: clock() };
    publish(run.state);
  };
  const refresh = () => {
    const current = ++run.generation;
    const controller = new AbortController();
    run.controller = controller;
    const finish = (data: T | null, error: string | null) => {
      if (run.stopped || current !== run.generation) return;
      ++run.generation;
      clearTimeout(run.deadline);
      run.state = error == null
        ? { data, receivedAt: clock(), now: clock(), error: null }
        : { ...run.state, error };
      emit();
      run.next = setTimeout(refresh, policy.pollMs);
    };
    run.deadline = setTimeout(() => {
      finish(null, "Explorer request timed out");
      controller.abort();
    }, policy.timeoutMs);
    Promise.resolve().then(() => {
      if (run.stopped) throw new Error("Observation stopped");
      return read(controller.signal);
    }).then(
      (data) => finish(data, null),
      () => finish(null, "Explorer API unreachable"),
    );
  };
  const ticker = setInterval(emit, policy.tickMs);
  refresh();
  return () => {
    run.stopped = true;
    ++run.generation;
    run.controller?.abort();
    clearTimeout(run.next);
    clearTimeout(run.deadline);
    clearInterval(ticker);
  };
}

export function connection(state: Observation<{ node_connected: boolean; indexer_lag_ms: number }>, policy = OBSERVATION_POLICY) {
  if (state.error) return { label: "unreachable", tone: "err" as const };
  if (!state.data) return { label: "checking", tone: "default" as const };
  if (cached(state, policy) || state.data.indexer_lag_ms + ageMs(state) >= policy.indexerWarnMs)
    return { label: "stale", tone: "warn" as const };
  return state.data.node_connected
    ? { label: "live", tone: "ok" as const }
    : { label: "node disconnected", tone: "warn" as const };
}

export function fleetHealth(now: number, last: number | null, policy = OBSERVATION_POLICY) {
  if (last == null) return { cls: "bg-mesh-err", label: "never seen" };
  const age = Math.max(0, now - last);
  if (age < policy.onlineMs) return { cls: "bg-mesh-ok", label: "online" };
  if (age < policy.idleMs) return { cls: "bg-mesh-warn", label: "idle" };
  return { cls: "bg-mesh-err", label: "offline" };
}
